import { useEffect, useState } from "react";

// A fluffy cloud built from overlapping blurred white circles
// Reduced from 5 to 3 blobs per cloud for GPU performance.
export const FluffyCloud = ({ size, opacity = 0.92, blur = 24, tint = "#ffffff" }) => {
  const blobs = [
    { left: "6%", top: "18%", w: "48%", h: "80%" },
    { left: "30%", top: "0%", w: "50%", h: "100%" },
    { left: "60%", top: "20%", w: "38%", h: "78%" },
  ];
  return (
    <div
      className="relative"
      style={{ width: size, height: size * 0.55 }}
      aria-hidden="true"
    >
      {blobs.map((b, i) => (
        <div
          key={i}
          className="absolute rounded-full"
          style={{ ...b, background: tint, opacity, filter: `blur(${blur}px)` }}
        />
      ))}
    </div>
  );
};

// Background cloud field — reduced count for smoother scroll on PC/mobile.
const CLOUDS = [
  { top: "-8vh", left: "-6%", size: 460, speed: 0.18, opacity: 1, blur: 24, tint: "#ffffff" },
  { top: "10vh", left: "62%", size: 360, speed: 0.32, opacity: 0.95, blur: 22, tint: "#ffffff" },
  { top: "42vh", left: "70%", size: 420, speed: 0.22, opacity: 1, blur: 24, tint: "#ffffff" },
  { top: "70vh", left: "-3%", size: 340, speed: 0.4, opacity: 0.95, blur: 22, tint: "#fff0e6" },
  { top: "110vh", left: "48%", size: 380, speed: 0.2, opacity: 0.95, blur: 24, tint: "#ffffff" },
  { top: "150vh", left: "12%", size: 380, speed: 0.28, opacity: 0.95, blur: 22, tint: "#ffffff" },
  { top: "195vh", left: "62%", size: 400, speed: 0.24, opacity: 0.95, blur: 24, tint: "#fff4ec" },
];

const WISPS = [
  { top: "24vh", left: "78%", size: 220, speed: 0.8, drift: -0.4, opacity: 0.5 },
  { top: "72vh", left: "-8%", size: 220, speed: 0.9, drift: 0.5, opacity: 0.45 },
];

export const CloudCanvas = () => {
  // Disable parallax on smaller screens & when user prefers reduced motion.
  const [enableParallax, setEnableParallax] = useState(true);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const check = () => {
      const reduced = mq.matches;
      const small = window.innerWidth < 640;
      setEnableParallax(!reduced && !small);
    };
    check();
    mq.addEventListener?.("change", check);
    window.addEventListener("resize", check);
    return () => {
      mq.removeEventListener?.("change", check);
      window.removeEventListener("resize", check);
    };
  }, []);

  useEffect(() => {
    if (!enableParallax) {
      document.documentElement.style.setProperty("--sy", "0");
      return;
    }
    const update = () => {
      document.documentElement.style.setProperty("--sy", String(window.scrollY));
    };
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        update();
        raf = null;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [enableParallax]);

  return (
    <>
      {/* Sky gradient — slightly more saturated */}
      <div
        aria-hidden="true"
        className="fixed inset-0 pointer-events-none"
        style={{
          zIndex: -2,
          background:
            "linear-gradient(180deg, #d8e7ee 0%, #f5e4e9 35%, #fff0dc 70%, #fbf2e6 100%)",
        }}
      />

      {/* Background clouds — CSS-var parallax (no React re-renders on scroll) */}
      <div
        aria-hidden="true"
        className="fixed inset-0 pointer-events-none overflow-hidden"
        style={{ zIndex: -1, contain: "layout paint" }}
      >
        {CLOUDS.map((c, i) => (
          <div
            key={i}
            className="absolute"
            style={{
              top: c.top,
              left: c.left,
              transform: `translate3d(0, calc(var(--sy, 0) * ${-c.speed} * 1px), 0)`,
              willChange: "transform",
            }}
          >
            <FluffyCloud size={c.size} opacity={c.opacity} blur={c.blur} tint={c.tint} />
          </div>
        ))}
      </div>

      {/* Foreground wisps — drift horizontally + parallax */}
      <div
        aria-hidden="true"
        className="fixed inset-0 pointer-events-none overflow-hidden"
        style={{ zIndex: 30, contain: "layout paint" }}
      >
        {WISPS.map((w, i) => (
          <div
            key={i}
            className="absolute"
            style={{
              top: w.top,
              left: w.left,
              transform: `translate3d(calc(var(--sy, 0) * ${w.drift} * 1px), calc(var(--sy, 0) * ${-w.speed} * 1px), 0)`,
              willChange: "transform",
            }}
          >
            <FluffyCloud size={w.size} opacity={w.opacity} blur={24} />
          </div>
        ))}
      </div>

      {/* Hero veil — single soft blob for opening effect */}
      <div
        aria-hidden="true"
        className="fixed inset-0 pointer-events-none overflow-hidden"
        style={{
          zIndex: 31,
          opacity: "max(0, calc(1 - var(--sy, 0) / 600))",
          contain: "layout paint",
        }}
      >
        <div
          className="absolute"
          style={{
            top: "42%",
            left: "62%",
            transform: "translate3d(calc(var(--sy, 0) * 0.6 * 1px), 0, 0)",
            willChange: "transform",
          }}
        >
          <FluffyCloud size={560} opacity={0.6} blur={32} />
        </div>
      </div>
    </>
  );
};

// Decorative floating clouds within a section. Use `relative` parent.
export const SectionClouds = ({ clouds = [] }) => (
  <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden">
    {clouds.map((c, i) => (
      <div
        key={i}
        className="absolute"
        style={{
          top: c.top,
          left: c.left,
          right: c.right,
          bottom: c.bottom,
          animation: `float-cloud ${c.duration || 12}s ease-in-out infinite`,
          animationDelay: `${c.delay || 0}s`,
        }}
      >
        <FluffyCloud
          size={c.size || 220}
          opacity={c.opacity || 0.85}
          blur={c.blur || 22}
          tint={c.tint || "#ffffff"}
        />
      </div>
    ))}
  </div>
);

export default CloudCanvas;
